import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";

// Features of the tenant's Airo licence, sent at sign-in as `airo_licenses`. Airo is a
// separate product: CORE means the tenant has a valid Airo licence at all (enough for the
// links that open Airo), QUERY_VERSE is what every "Ask Airo" control needs.
export const AIRO_CORE = "core";
export const AIRO_QUERY_VERSE = "query_verse";

// Shown on a locked Ask Airo control.
export const AIRO_LOCKED_TEXT = "Ask Airo is not included in your licence";

/**
 * True when the tenant's Airo licence includes this feature. Reads the user's details
 * stored at sign-in, so it never makes a request. False if none or unreadable.
 */
export function isAiroLicensed(feature: string): boolean {
  try {
    const permission = JSON.parse(localStorage.getItem("permission") || "{}");
    return (permission?.airo_licenses || []).includes(feature);
  } catch {
    return false;
  }
}

// Where the super-user licence notice is kept between page loads.
const NOTICE_KEY = "licenceNotice";

/**
 * Which modules the tenant's licence unlocks, for locking menu items and landing tiles,
 * and the super-user licence notice shown in the header.
 *
 * Reads `tenant_licenses`, the module names sent with the user's details at sign-in and
 * stored in localStorage["permission"]. An item without a module is never locked.
 */
@Injectable({ providedIn: "root" })
export class LicenseService {
  constructor(private readonly http: HttpClient) {}

  /** The licensed module names, e.g. "module.risknet". Empty if none or unreadable. */
  licensedModules(): Set<string> {
    try {
      const permission = JSON.parse(localStorage.getItem("permission") || "{}");
      return new Set<string>(permission?.tenant_licenses || []);
    } catch {
      return new Set<string>();
    }
  }

  /**
   * True when an item may be used: it has no module, or its module is licensed.
   * Pass `licensed` when checking many items, so the stored details are read once.
   */
  isLicensed(module?: string | null, licensed: Set<string> = this.licensedModules()): boolean {
    return !module || licensed.has(module);
  }

  /** True when the tenant's Airo licence includes this feature; see isAiroLicensed. */
  isAiroLicensed(feature: string): boolean {
    return isAiroLicensed(feature);
  }

  /**
   * The super-user licence notice ("Licence expires in N days").
   *
   * Fetched once per sign-in per day and kept in localStorage, so every page load and
   * reload shows it straight away instead of blanking until the API answers. Fetched
   * again after a new sign-in (a different token) or on a new day, so the day count
   * stays current. A failed call is not saved, so the next page load tries again.
   */
  licenceNotice(): Observable<any> {
    const token = localStorage.getItem("token") ?? "";
    const today = new Date().toDateString();

    try {
      const saved = JSON.parse(localStorage.getItem(NOTICE_KEY) ?? "null");
      if (saved && saved.token === token && saved.day === today) {
        return of(saved.notice);
      }
    } catch {
      // Unreadable: fetch it again below.
    }

    return this.http.get("licensing/notice/").pipe(
      map((notice) => {
        localStorage.setItem(NOTICE_KEY, JSON.stringify({ token, day: today, notice }));
        return notice;
      }),
      catchError(() => of(null))
    );
  }
}
